// CorplistFormModal.jsx
import React, { useState, useContext } from "react";
import ReactModal from "react-modal";
import { FormProvider } from "react-hook-form";
import { Input, Select, Draggable } from "@components";
import { useCompanyFormModal } from "@hooks";
import { AuthContext } from "@context";
import { ROLES } from "@constants/roles";
import "./CorpregCustom.css";

const CorplistFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  isEdit,
  boardNum,
}) => {
  const { auth } = useContext(AuthContext);
  const isSuperAdmin = auth.role === ROLES.SUPER_ADMIN;

  const {
    handleFormSubmit,
    setValue,
    watch,
    reset,
    loadingDetail,
    detailError,
    formState: { errors },
    ...methods
  } = useCompanyFormModal({
    isOpen,
    isEdit,
    boardNum,
    onClose,
    onSuccess,
    isSuperAdmin,
  });

  const position = Draggable({ isOpen, dragHandle: ".modal-header" });
  const containerStyle = {
    maxWidth: "1150px",
    minWidth: "1150px",
    maxHeight: "820px",
    minHeight: "820px",
    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`,
  };

  const [isComposing, setIsComposing] = useState(false);

  const handleNumericKeyDown = (e) => {
    if (isComposing) return;
    const allowKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"];
    if (allowKeys.includes(e.key)) return;
    if (e.shiftKey || e.altKey || e.ctrlKey) { e.preventDefault(); return; }
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const handleNumericPaste = (e) => {
    e.preventDefault();
    const onlyDigits = (e.clipboardData.getData("text/plain") || "").replace(/\D/g, "");
    setValue(e.target.name, onlyDigits, { shouldValidate: true });
  };

  const handleNumericDrop = (e) => {
    e.preventDefault();
    const onlyDigits = (e.dataTransfer.getData("text/plain") || "").replace(/\D/g, "");
    setValue(e.target.name, onlyDigits, { shouldValidate: true });
  };

  const handleCompositionStart = () => setIsComposing(true);
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    const onlyDigits = e.target.value.replace(/\D/g, "");
    e.target.value = onlyDigits;
    setValue(e.target.name, onlyDigits, { shouldValidate: true });
  };

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setValue("addr1", data.address, { shouldValidate: true });
      },
    }).open();
  };

  const regionList = watch("regionList") || [];
  const sizeCategoryList = watch("sizeCategoryList") || [];
  const bizTypeList = watch("bizTypeList") || [];
  const managerList = watch("managerList") || [];
  const selectedManagerIds = watch("managerIds") || [];

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="modal-overlay"
      className="modal-wrap"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmit}>
          <div className="modal-container" style={containerStyle}>
            <div className="modal-header" style={{ cursor: "move" }}>
              <h5>
                회사 정보{" "}
                <span className="modal-header-span">
                  {isEdit ? "수정" : "등록"}
                </span>
              </h5>
              <button type="button" className="modal-btn-close" onClick={onClose}>
                <span className="modal-btn-span">X</span>
              </button>
            </div>

            <div className="modal-content">
              {loadingDetail && isEdit ? (
                <div>데이터 로딩 중...</div>
              ) : detailError ? (
                <div>오류 발생: {detailError.message}</div>
              ) : (
                <table className="table-write" style={{ width: "100%" }}>
                  <tbody>
                    <tr>
                      <th>회사 코드</th>
                      <td>
                        <Input
                          name="compCode"
                          readOnly
                          className="input-customtext"
                          style={{ width: "200px" }}
                        />
                      </td>
                      <th>관할구역<span className="requiredcustom">*</span></th>
                      <td>
                        <Select
                          name="region"
                          options={regionList.map((r) => ({ key: r.comdCode, name: r.comdName }))}
                          defaultOption="관할구역 선택"
                          className={`input-customselect ${errors.region ? "error" : ""}`}
                          style={{ width: "200px" }}
                        />
                        {errors.region && (
                          <p className="error-custommessage">{errors.region.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>사업자등록번호<span className="requiredcustom">*</span></th>
                      <td>
                        <Input
                          name="bizNo"
                          placeholder="사업자등록번호(- 제외)"
                          maxLength={10}
                          className={`input-customtext ${errors.bizNo ? "error" : ""}`}
                          onKeyDown={handleNumericKeyDown}
                          onPaste={handleNumericPaste}
                          onDrop={handleNumericDrop}
                          onCompositionStart={handleCompositionStart}
                          onCompositionEnd={handleCompositionEnd}
                        />
                        {errors.bizNo && (
                          <p className="error-custommessage">{errors.bizNo.message}</p>
                        )}
                      </td>
                      <th>법인명<span className="requiredcustom">*</span></th>
                      <td>
                        <Input
                          name="compName"
                          placeholder="법인명"
                          maxLength={20}
                          className={`input-customtext ${errors.compName ? "error" : ""}`}
                        />
                        {errors.compName && (
                          <p className="error-custommessage">{errors.compName.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>대표자명<span className="requiredcustom">*</span></th>
                      <td>
                        <Input
                          name="repName"
                          placeholder="대표자명"
                          maxLength={20}
                          className={`input-customtext ${errors.repName ? "error" : ""}`}
                        />
                        {errors.repName && (
                          <p className="error-custommessage">{errors.repName.message}</p>
                        )}
                      </td>
                      <th>전화번호</th>
                      <td>
                        <Input
                          name="phone"
                          placeholder="전화번호(- 제외)"
                          maxLength={11}
                          className={`input-customtext ${errors.phone ? "error" : ""}`}
                          onKeyDown={handleNumericKeyDown}
                          onPaste={handleNumericPaste}
                          onDrop={handleNumericDrop}
                          onCompositionStart={handleCompositionStart}
                          onCompositionEnd={handleCompositionEnd}
                        />
                        <p style={{ fontSize: '11px', color: '#888', margin: '3px 0 0' }}>- 없이 숫자만 입력하세요.</p>
                        {errors.phone && (
                          <p className="error-custommessage">{errors.phone.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>주소<span className="requiredcustom">*</span></th>
                      <td colSpan={3}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Input
                              name="addr1"
                              placeholder="주소를 검색하세요"
                              className={`input-customtext ${errors.addr1 ? "error" : ""}`}
                              style={{ width: "400px" }}
                            />
                            <button
                              type="button"
                              className="btn-save"
                              style={{ marginLeft: "8px" }}
                              onClick={handleAddressSearch}
                            >
                              검색
                            </button>
                          </div>
                          {errors.addr1 && (
                            <p className="error-custommessage">{errors.addr1.message}</p>
                          )}
                          <Input
                            name="addr2"
                            placeholder="상세주소"
                            maxLength={50}
                            className={`input-customtext ${errors.addr2 ? "error" : ""}`}
                            style={{ width: "400px" }}
                          />
                          {errors.addr2 && (
                            <p className="error-custommessage">{errors.addr2.message}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>사내규모<span className="requiredcustom">*</span></th>
                      <td>
                        <Select
                          name="sizeCategory"
                          options={sizeCategoryList.map((s) => ({ key: s.comdCode, name: s.comdName }))}
                          defaultOption="선택"
                          className={`input-customselect ${errors.sizeCategory ? "error" : ""}`}
                          style={{ width: "200px" }}
                        />
                        {errors.sizeCategory && (
                          <p className="error-custommessage">{errors.sizeCategory.message}</p>
                        )}
                      </td>
                      <th>업태<span className="requiredcustom">*</span></th>
                      <td>
                        <Select
                          name="bizType"
                          options={bizTypeList.map((b) => ({ key: b.comdCode, name: b.comdName }))}
                          defaultOption="선택"
                          className={`input-customselect ${errors.bizType ? "error" : ""}`}
                          style={{ width: "200px" }}
                        />
                        {errors.bizType && (
                          <p className="error-custommessage">{errors.bizType.message}</p>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>종목</th>
                      <td>
                        <Input
                          name="bizItem"
                          placeholder="종목"
                          maxLength={50}
                          className="input-customtext"
                        />
                      </td>
                      <td colSpan={2} />
                    </tr>
                    {/* 등록 모드에서만 ID 회사명 표시 */}
                    {!isEdit && (
                      <tr>
                        <th>ID 회사명<span className="requiredcustom">*</span><br/><span style={{fontSize:'11px',fontWeight:'normal',color:'#888'}}>(영문·한글, 특수문자 제외)</span></th>
                        <td colSpan={3}>
                          <Input
                            name="engCompName"
                            placeholder="예: 아토스, atos"
                            maxLength={100}
                            className={`input-customtext ${errors.engCompName ? "error" : ""}`}
                            style={{ width: "300px" }}
                          />
                          {errors.engCompName && (
                            <p className="error-custommessage">{errors.engCompName.message}</p>
                          )}
                          <span style={{ marginLeft: "8px", fontSize: "12px", color: "#666" }}>
                            전화번호 뒤 4자리가 초기 비밀번호가 됩니다.
                          </span>
                        </td>
                      </tr>
                    )}
                    {/* 담당자(슈퍼관리자 전용) + 사용여부(수정 모드 전용) */}
                    {(isSuperAdmin || isEdit) && (
                      <tr>
                        {isSuperAdmin ? (
                          <>
                            <th>담당자<br /><span style={{ fontWeight: "normal", fontSize: "11px", color: "#888" }}>(복수 선택 가능)</span></th>
                            <td>
                              <div
                                style={{
                                  maxHeight: "130px",
                                  overflowY: "auto",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  padding: "6px 10px",
                                  minWidth: "200px",
                                  background: "#fff",
                                }}
                              >
                                {managerList.length === 0 ? (
                                  <span style={{ color: "#999", fontSize: "12px" }}>담당자 없음</span>
                                ) : (
                                  managerList.map((m) => {
                                    const currentIds = selectedManagerIds;
                                    const checked = selectedManagerIds.includes(m.id);
                                    return (
                                      <label
                                        key={m.id}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "6px",
                                          marginBottom: "4px",
                                          cursor: "pointer",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          value={m.id}
                                          checked={checked}
                                          onChange={(e) => {
                                            const ids = selectedManagerIds;
                                            if (e.target.checked) {
                                              setValue("managerIds", [...ids, m.id]);
                                            } else {
                                              setValue("managerIds", ids.filter((id) => id !== m.id));
                                            }
                                          }}
                                        />
                                        {m.name}
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                            </td>
                          </>
                        ) : (
                          <td colSpan={2} />
                        )}
                        {isEdit && (
                          <>
                            <th>사용여부</th>
                            <td>
                              <Select
                                name="useYn"
                                options={[
                                  { key: "Y", name: "사용" },
                                  { key: "N", name: "미사용" },
                                ]}
                                className="input-customselect"
                                style={{ width: "120px" }}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    )}
                    <tr>
                      <th>비고</th>
                      <td colSpan={3}>
                        <div style={{ textAlign: "right", marginBottom: "4px" }}>
                          {watch("notice")?.length || 0}/2000
                        </div>
                        <textarea
                          name="notice"
                          placeholder="비고"
                          maxLength={2000}
                          className={`input-customtextarea ${errors.notice ? "error" : ""}`}
                          rows={3}
                          style={{ width: "100%" }}
                          {...methods.register("notice")}
                        />
                        {errors.notice && (
                          <p className="error-custommessage">{errors.notice.message}</p>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            <div className="modal-btn-wrap btn-right-box">
              <ul>
                <li>
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    취소
                  </button>
                </li>
                <li>
                  <button type="submit" className="btn-save">
                    저장
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </form>
      </FormProvider>
    </ReactModal>
  );
};

export { CorplistFormModal };
